import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './TimelineBlock.module.css';

// ====== helpers ======
const STATUS_COLOR = {
  Закрыта: '#16a34a',
  'Не доставлено': '#ef4444',
  Открыта: '#2563eb',
};
const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
const toHour = (d) => d.getHours() + d.getMinutes() / 60 + d.getSeconds() / 3600;

const getReceiptTime = (r, includeProducts) => {
  if (!r) return null;

  if (includeProducts && Array.isArray(r.products) && r.products.length) {
    const ts = r.products
      .map(p => {
        const u = p?.updatedAt ? new Date(p.updatedAt).getTime() : 0;
        const c = p?.createdAt ? new Date(p.createdAt).getTime() : 0;
        return Math.max(u, c);
      })
      .filter(Boolean);
    if (ts.length) return new Date(Math.max(...ts));
  }

  if (r.status_changed_at) {
    const d = new Date(r.status_changed_at);
    if (!isNaN(d)) return d;
  }
  return null;
};

// Шаг делений
const pickTickMinutes = (span) => {
  if (span > 12) return 60;
  if (span > 6)  return 30;
  if (span > 3)  return 15;
  if (span > 1)  return 5;
  return 1;
};

export default function TimelineBlock({
  routes = [],                 
  initialWindow = [0, 24],
  defaultIncludeProducts = false,
  height = 420,
  onMarkerClick,               
  onRouteClick,                
}) {
  const [includeProducts, setIncludeProducts] = useState(!!defaultIncludeProducts);
  const [windowStart, setWindowStart] = useState(initialWindow[0]);
  const [windowEnd, setWindowEnd] = useState(initialWindow[1]);
  const span = windowEnd - windowStart;

  const gridRef = useRef(null);
  const drag = useRef({ active: false, startX: 0, startWindow: [0, 24] });
  const [noSelect, setNoSelect] = useState(false);

  // ==== NEW: пагинация ====
  const [page, setPage] = useState(1);
  const perPage = 10;
  const totalPages = Math.max(1, Math.ceil(routes.length / perPage));

  const pagedRoutes = useMemo(() => {
    const startIdx = (page - 1) * perPage;
    return routes.slice(startIdx, startIdx + perPage);
  }, [routes, page]);

  // События на маршрутах
  const eventsByRoute = useMemo(() => {
    return pagedRoutes.map((r) => {
      const routeNumber = r?.route_number ?? r?.routeNumber ?? r?.id ?? '—';
      const receipts = Array.isArray(r?.receipts) ? r.receipts : [];
      const events = [];

      for (const receipt of receipts) {
        const t = getReceiptTime(receipt, includeProducts);
        if (!t) continue;
        const hour = toHour(t);
        if (hour < windowStart || hour > windowEnd) continue;

        events.push({
          hour,
          date: t,
          status: receipt.status,
          label: `№${receipt.receipt_number ?? receipt.id ?? ''}`,
          address: receipt.address ?? receipt.delivery_address,
          receipt,
          route: r,
        });
      }

      events.sort((a, b) => a.hour - b.hour);
      for (let i = 1; i < events.length; i++) {
        const diff = events[i].hour - events[i - 1].hour;
        const dh = Math.floor(diff);
        const dm = Math.round((diff - dh) * 60);
        events[i].delta = `${dh}ч ${dm}м`;
      }

      return { route: r, routeNumber, events };
    });
  }, [pagedRoutes, includeProducts, windowStart, windowEnd]);

  // Деления
  const ticks = useMemo(() => {
    const mStep = pickTickMinutes(span);
    const startM = Math.ceil((windowStart * 60) / mStep) * mStep;
    const endM = Math.floor((windowEnd * 60) / mStep) * mStep;
    const res = [];
    for (let m = startM; m <= endM; m += mStep) {
      const h = Math.floor(m / 60), mm = m % 60;
      const frac = (m / 60 - windowStart) / span;
      res.push({ frac, label: `${String(h).padStart(2,'0')}:${String(mm).padStart(2,'0')}` });
    }
    return res;
  }, [windowStart, windowEnd, span]);

  const resetWindow = () => { setWindowStart(0); setWindowEnd(24); };

  // Зум
  const onWheelCapture = (e) => {
    const zoomGesture = e.shiftKey;
    if (!zoomGesture) return;

    e.preventDefault();
    e.stopPropagation();

    const host = gridRef.current;
    if (!host) return;
    const rect = host.getBoundingClientRect();
    const frac = clamp((e.clientX - rect.left) / rect.width, 0, 1);

    const factor = 1 + Math.sign(e.deltaY) * 0.15;
    const newSpan = clamp(span * factor, 0.25, 24);
    const center = windowStart + frac * span;

    let start = center - frac * newSpan;
    let end = start + newSpan;
    if (start < 0) { end -= start; start = 0; }
    if (end > 24) { start -= (end - 24); end = 24; }

    setWindowStart(start);
    setWindowEnd(end);
  };

  // Пан
  const onMouseDown = (e) => {
    if (span >= 24) return;
    drag.current = { active: true, startX: e.clientX, startWindow: [windowStart, windowEnd] };
    gridRef.current?.classList.add(styles.dragging);
    setNoSelect(true);
    document.body.style.userSelect = 'none';
  };
  const onMouseMove = (e) => {
    if (!drag.current.active || !gridRef.current) return;
    const { width } = gridRef.current.getBoundingClientRect();
    const dx = e.clientX - drag.current.startX;
    const deltaHours = (dx / width) * (drag.current.startWindow[1] - drag.current.startWindow[0]);
    let start = drag.current.startWindow[0] - deltaHours;
    let end = drag.current.startWindow[1] - deltaHours;
    if (start < 0) { end -= start; start = 0; }
    if (end > 24) { start -= (end - 24); end = 24; }
    setWindowStart(start); setWindowEnd(end);
  };
  const onMouseUp = () => {
    if (!drag.current.active) return;
    drag.current.active = false;
    gridRef.current?.classList.remove(styles.dragging);
    setNoSelect(false);
    document.body.style.userSelect = '';
  };

  return (
    <div className={styles.card} style={{ height }}>
      <div className={styles.header}>
        <div className={styles.title}>Таймлайн</div>
        
        {/* центр: пагинация */}
        <div className={styles.pagination}>
        <button 
            className={styles.pageBtn}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
        >
            ‹
        </button>

        <span>страница {page} / {totalPages}</span>

        <button 
            className={styles.pageBtn}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
        >
            ›
        </button>
        </div>

        <div className={styles.headerControls}>
          <label className={styles.chk}>
            <input
              type="checkbox"
              checked={includeProducts}
              onChange={(e) => setIncludeProducts(e.target.checked)}
            />
            по сканам товаров
          </label>
          <button className={styles.btn} onClick={resetWindow}>Сброс</button>
        </div>
      </div>

      {/* ЕДИНАЯ скролл-область */}
      <div
        className={`${styles.scrollArea} ${noSelect ? styles.noSelect : ''}`}
        style={{ maxHeight: height - 46 }}
        onWheelCapture={onWheelCapture}
        onMouseDownCapture={(e) => { if (e.shiftKey) e.preventDefault(); }}
        onDragStart={(e) => e.preventDefault()}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseUp}
        onMouseUp={onMouseUp}
      >
        {/* Sticky верхняя шкала */}
        <div className={styles.ticksRow}>
          <div className={styles.gridHost} ref={gridRef}>
            {ticks.map((t, idx) => (
              <div key={idx} className={styles.tick} style={{ left: `${t.frac * 100}%` }}>
                <span className={styles.tickLabel}>{t.label}</span>
                <span className={styles.vline} />
              </div>
            ))}
          </div>
        </div>

        {/* Список маршрутов */}
        <div className={styles.rowsGrid}>
          {eventsByRoute.map(({ route, routeNumber, events }, ri) => (
            <div key={route?.id ?? routeNumber ?? ri} className={styles.row}>
              <button
                className={styles.labelCell}
                onClick={() => onRouteClick?.(route)}
                title="Показать только этот маршрут"
              >
                Маршрут №{routeNumber}
              </button>

              <div className={styles.gridCell}>
                <div className={styles.hline} />
                <div className={styles.gridHost}>
                  {events.map((e, i) => {
                    const leftPct = ((e.hour - windowStart) / span) * 100;
                    return (
                      <div
                        key={i}
                        className={styles.marker}
                        style={{ left: `${leftPct}%`, backgroundColor: STATUS_COLOR[e.status] || '#9CA3AF' }}
                        onClick={() => onMarkerClick?.(e.receipt, route)}
                      >
                        <div className={styles.tooltip}>
                          <b>{e.label}</b><br />
                          {e.date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}<br />
                          Статус: {e.status}<br />
                          {e.delta && <>Δ {e.delta}<br /></>}
                          {e.address && <>Адрес: {e.address}</>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
          {!routes.length && <div className={styles.empty}>Нет данных для выбранного периода</div>}
        </div>
      </div>

      <div className={styles.footerHint}>
        Shift + колесо — масштаб, перетаскивание — пан. Диапазон: {windowStart.toFixed(1)}–{windowEnd.toFixed(1)} ч.
      </div>
    </div>
  );
}
